import type { ReactNode } from "react"
import { formatDateString } from "@/v2-common/utils/utils.date"
import LicensePackageBadges from "@/v2-backoffice-client/license/changes/LicensePackageBadges"
import LicenseProvenance from "@/v2-backoffice-client/license/changes/LicenseProvenance"
import {
  hasProvenance,
  isLockedChange,
  isPlaceholderName,
  lockPlacementOf,
  packagesOf,
  whereOf,
  type LicenseChange,
} from "@/v2-backoffice-client/license/changes/license.changes.utils"

export type LicenseChangeDetailProps = {
  change: LicenseChange
}

type Fact = { label: string; value: ReactNode }

// The capability panel's Details tab: what the capability is in the words
// its own text uses, where it lives, what a lock would do, where it is
// defined (in the app's code, by developers: the answer to "can I change
// it here?"), and, for whoever needs the trail, the release, issue, pull
// request and commit it came from. Everything the row and the header leave
// out so both can be read at a glance.
function LicenseChangeDetail(props: LicenseChangeDetailProps) {
  const { change } = props
  const packages = packagesOf(change)
  const isLocked = isLockedChange(change)
  const facts: Fact[] = [
    { label: "Where in Fusion", value: whereOf(change) },
    {
      label: isLocked ? "Included in" : "Placed in",
      value: (
        <span className="flex flex-col gap-1">
          <LicensePackageBadges
            names={packages}
            isNested
            variant={isLocked ? "neutral" : "outline"}
            emptyLabel="No package yet"
            isEmptyCritical={isLocked}
          />
          <span className="text-muted-foreground">
            {isLocked
              ? "Customers get it through any one of these."
              : packages.length > 0
                ? "Changes nothing while it is free: these are the packages that keep it the day it is made package-only."
                : "Package-only will ask which packages should carry it; Free for everyone needs none."}
          </span>
        </span>
      ),
    },
    ...(change.IsInCatalog
      ? [
          {
            label: isLocked ? "The lock" : "If package-only",
            value: `${lockPlacementOf(change)} To see it before deciding, ask a developer to force it in Fusion's dev toolbar → Locks (this capability by code), which locks it for their session only.`,
          },
        ]
      : []),
    {
      label: "Defined in",
      value:
        "Fusion's code, by developers: the component declares it, and every deploy's scan refreshes this list. Renaming, splitting or removing it is a code change.",
    },
    ...(change.Detail ? [{ label: "What moved", value: change.Detail }] : []),
    {
      label: "Noticed",
      value: `${formatDateString(change.ChangedAt)}, when a deploy's scan of the app first saw it`,
    },
    ...(hasProvenance(change)
      ? [
          {
            label: "Shipped in",
            value: <LicenseProvenance provenance={change} />,
          },
        ]
      : []),
  ]

  return (
    <div className="flex flex-col gap-3 text-xs">
      <p className="text-sm text-muted-foreground">
        {change.Description ??
          (isPlaceholderName(change)
            ? "No description has been written for this capability yet."
            : "No description yet.")}
      </p>
      <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-2">
        {facts.map((fact) => (
          <div key={fact.label} className="contents">
            <dt className="text-muted-foreground">{fact.label}</dt>
            <dd className="min-w-0">{fact.value}</dd>
          </div>
        ))}
      </dl>
    </div>
  )
}

export default LicenseChangeDetail
